// src/page/StamprallyDetail.js - 参照専用画面（運用状況）
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import useAuthGuard from "../hooks/useAuthGuard";
import Layout from "../components/Layout";
import { API_BASE_URL } from "../config";

const pad2 = (n) => String(n).padStart(2, "0");
const fmtDateTime = (s) => {
  if (!s) return "-";
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return String(s);
  return `${d.getFullYear()}/${pad2(d.getMonth() + 1)}/${pad2(d.getDate())} ${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
};

const fmtDate = (s) => {
  if (!s) return "-";
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return String(s);
  return `${d.getFullYear()}/${pad2(d.getMonth() + 1)}/${pad2(d.getDate())}`;
};

export default function StamprallyDetail() {
  useAuthGuard();
  const navigate = useNavigate();
  const { stamprallyID } = useParams();

  const MSG = {
    apiError: "サーバーエラーが発生しました",
    notFound: "スタンプラリーが見つかりません",
  };

  // 基本データ
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [stamprally, setStamprally] = useState(null);
  const [activeTab, setActiveTab] = useState("reward"); // reward | activity

  // 特典状況タブ用
  const [rewardData, setRewardData] = useState([]);
  const [rewardLoading, setRewardLoading] = useState(false);
  const [rewardSearch, setRewardSearch] = useState({
    periodStart: "",
    periodEnd: "",
    status: "",
    userId: "",
  });
  const [rewardSummary, setRewardSummary] = useState({
    total: 0,
    exchanged: 0,
    unexchanged: 0,
    granted: 0,
  });

  // ユーザー稼働状況タブ用
  const [activityData, setActivityData] = useState([]);
  const [activityLoading, setActivityLoading] = useState(false);
  const [activitySearch, setActivitySearch] = useState({
    userId: "",
  });
  const [selectedUser, setSelectedUser] = useState(null);
  const [showUserDetail, setShowUserDetail] = useState(false);
  const [markingAchieved, setMarkingAchieved] = useState(false);
  const [markAchievedMessage, setMarkAchievedMessage] = useState({ type: '', text: '' });
  const [expandedConditions, setExpandedConditions] = useState(new Set());

  // ========== データ読み込み ==========

  // スタンプラリー基本情報取得
  useEffect(() => {
    const loadStamprally = async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem("access_token");
        const res = await fetch(`${API_BASE_URL}/v1/stamprallies/${stamprallyID}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.status === 404) {
          setError(MSG.notFound);
          setLoading(false);
          return;
        }
        if (!res.ok) throw new Error(MSG.apiError);
        const data = await res.json();
        setStamprally(data);
        setError("");
      } catch (e) {
        console.error(e);
        setError(MSG.apiError);
      } finally {
        setLoading(false);
      }
    };

    loadStamprally();
  }, [stamprallyID]);

  // 特典状況データ取得
  const loadRewardData = async () => {
    setRewardLoading(true);
    try {
      const token = localStorage.getItem("access_token");
      const params = new URLSearchParams();
      if (rewardSearch.periodStart) params.append("periodStart", rewardSearch.periodStart);
      if (rewardSearch.periodEnd) params.append("periodEnd", rewardSearch.periodEnd);
      if (rewardSearch.status) params.append("status", rewardSearch.status);
      if (rewardSearch.userId) params.append("userId", rewardSearch.userId);
      const res = await fetch(
        `${API_BASE_URL}/v1/stamprallies/${stamprallyID}/reward-status?${params}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      if (res.ok) {
        const data = await res.json();
        setRewardData(data.items || []);
        setRewardSummary({
          total: data.total || 0,
          exchanged: data.exchanged || 0,
          unexchanged: data.unexchanged || 0,
          granted: data.granted || 0,
        });
      }
    } catch (e) {
      console.error("特典状況読み込みエラー:", e);
    } finally {
      setRewardLoading(false);
    }
  };

  // ユーザー稼働状況データ取得
  const loadActivityData = async () => {
    setActivityLoading(true);
    try {
      const token = localStorage.getItem("access_token");
      const params = new URLSearchParams();
      if (activitySearch.userId) params.append("userId", activitySearch.userId);
      const res = await fetch(
        `${API_BASE_URL}/v1/stamprallies/${stamprallyID}/user-activity?${params}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      if (res.ok) {
        const data = await res.json();
        setActivityData(data.items || []);
      }
    } catch (e) {
      console.error("ユーザー稼働状況読み込みエラー:", e);
    } finally {
      setActivityLoading(false);
    }
  };

  // タブ切り替え時のデータ読み込み
  useEffect(() => {
    if (activeTab === "reward" && stamprallyID) {
      loadRewardData();
    } else if (activeTab === "activity" && stamprallyID) {
      loadActivityData();
    }
  }, [activeTab, stamprallyID]);

  // ========== CSV出力 ==========

  const downloadCSV = (data, filename, headers) => {
    const csvContent = [
      headers.join(","),
      ...data.map((row) =>
        headers.map((h) => `"${(row[h] || "").toString().replace(/"/g, '""')}"`).join(",")
      ),
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = filename;
    link.click();
  };

  const handleRewardCSV = () => {
    const rewardType = stamprally?.rewardType;
    let headers, filename;

    switch (rewardType) {
      case "apply":
        headers = ["userId", "status", "date", "content"];
        filename = `stamprally_${stamprallyID}_applications.csv`;
        break;
      case "store":
        headers = ["userId", "store", "date", "coupon"];
        filename = `stamprally_${stamprallyID}_exchanges.csv`;
        break;
      case "auto":
        headers = ["userId", "status", "date", "content", "failureReason"];
        filename = `stamprally_${stamprallyID}_grants.csv`;
        break;
      default:
        headers = ["userId", "status", "date", "content"];
        filename = `stamprally_${stamprallyID}_reward.csv`;
    }

    downloadCSV(rewardData, filename, headers);
  };

  const handleActivityCSV = () => {
    downloadCSV(activityData, `stamprally_${stamprallyID}_activity.csv`, [
      "userId",
      "achievementStatus",
      "achievementDate",
      "details",
    ]);
  };

  // ========== ユーザー詳細表示 ==========

  const handleShowUserDetail = (user) => {
    setSelectedUser(user);
    setShowUserDetail(true);
  };

  const handleCloseUserDetail = () => {
    setShowUserDetail(false);
    setSelectedUser(null);
    setMarkAchievedMessage({ type: '', text: '' });
    setExpandedConditions(new Set());
  };

  // 条件詳細の展開/折りたたみ
  const toggleConditionDetails = (conditionId) => {
    setExpandedConditions(prev => {
      const newSet = new Set(prev);
      if (newSet.has(conditionId)) {
        newSet.delete(conditionId);
      } else {
        newSet.add(conditionId);
      }
      return newSet;
    });
  };

  // 条件達成手動登録
  const handleMarkAchieved = async (userId, conditionId) => {
    setMarkingAchieved(true);
    setMarkAchievedMessage({ type: '', text: '' });
    
    try {
      const token = localStorage.getItem('access_token');
      const res = await fetch(
        `${API_BASE_URL}/v1/stamprallies/${stamprallyID}/users/${userId}/conditions/${conditionId}/achieve`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (res.status === 409) {
        setMarkAchievedMessage({ type: 'warning', text: '既に達成済みです' });
        return;
      }
      
      if (res.status === 404) {
        setMarkAchievedMessage({ type: 'error', text: 'データが見つかりません' });
        return;
      }
      
      if (!res.ok) {
        throw new Error(MSG.apiError);
      }

      const data = await res.json();
      setMarkAchievedMessage({ type: 'success', text: data.message || '達成を登録しました' });
      
      // ユーザー活動データを再取得
      await loadActivityData();
      
      // 選択中のユーザーデータも更新
      const updatedRes = await fetch(
        `${API_BASE_URL}/v1/stamprallies/${stamprallyID}/user-activity?userId=${userId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      
      if (updatedRes.ok) {
        const updatedData = await updatedRes.json();
        if (updatedData.items && updatedData.items.length > 0) {
          setSelectedUser(updatedData.items[0]);
        }
      }
    } catch (e) {
      console.error(e);
      setMarkAchievedMessage({ type: 'error', text: e.message || MSG.apiError });
    } finally {
      setMarkingAchieved(false);
    }
  };

  // ========== レンダリング ==========

  if (loading) {
    return (
      <Layout>
        <div className="content">
          <div className="container-fluid p-4">
            <div className="text-center py-5">
              <div className="spinner-border" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <p className="mt-3">データを読み込んでいます...</p>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout>
        <div className="content">
          <div className="container-fluid p-4">
            <div className="alert alert-danger">{error}</div>
            <button
              className="btn btn-secondary"
              onClick={() => navigate("/stamprally")}
            >
              一覧に戻る
            </button>
          </div>
        </div>
      </Layout>
    );
  }

  const rewardType = stamprally?.rewardType;
  const rewardTypeLabel = {
    apply: "応募型",
    store: "店舗交換型",
    // auto: "自動付与型", // 今フェーズでは非表示
  }[rewardType] || "不明";

  return (
    <Layout>
      <div className="content">
        <div className="container-fluid p-4">
          {/* ヘッダー */}
          <div className="d-flex justify-content-between align-items-center mb-4">
            <div>
              <nav aria-label="breadcrumb">
                <ol className="breadcrumb mb-1">
                  <li className="breadcrumb-item">
                    <a href="/stamprally">スタンプラリー</a>
                  </li>
                  <li className="breadcrumb-item active">詳細（運用状況）</li>
                </ol>
              </nav>
              <h2 className="mb-0">
                {stamprally?.stamprallyName || "スタンプラリー詳細"}
              </h2>
              <small className="text-muted">
                特典タイプ: {rewardTypeLabel}
              </small>
            </div>
            <div className="d-flex gap-2">
              <button
                className="btn btn-outline-primary"
                onClick={() => navigate(`/stamprally/${stamprallyID}/edit`)}
              >
                編集画面へ
              </button>
              <button
                className="btn btn-secondary"
                onClick={() => navigate("/stamprally")}
              >
                一覧に戻る
              </button>
            </div>
          </div>

          {/* タブナビゲーション */}
          <ul className="nav nav-tabs mb-4">
            <li className="nav-item">
              <button
                className={`nav-link ${activeTab === "reward" ? "active" : ""}`}
                onClick={() => setActiveTab("reward")}
              >
                特典状況
              </button>
            </li>
            <li className="nav-item">
              <button
                className={`nav-link ${activeTab === "activity" ? "active" : ""}`}
                onClick={() => setActiveTab("activity")}
              >
                ユーザー稼働状況
              </button>
            </li>
          </ul>

          {/* ========== 特典状況タブ ========== */}
          {activeTab === "reward" && (
            <div className="card">
              <div className="card-header d-flex justify-content-between align-items-center">
                <h5 className="mb-0">特典状況</h5>
                <button
                  className="btn btn-outline-secondary btn-sm"
                  onClick={handleRewardCSV}
                  disabled={rewardData.length === 0}
                >
                  <i className="fas fa-download me-1"></i>
                  CSV出力
                </button>
              </div>
              <div className="card-body">
                {/* サマリーカード */}
                <div className="row mb-4">
                  <div className="col-md-3">
                    <div className="card bg-light">
                      <div className="card-body text-center">
                        <h3 className="mb-1">{rewardSummary.total}</h3>
                        <small className="text-muted">総応募数</small>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-3">
                    <div className="card bg-light">
                      <div className="card-body text-center">
                        <h3 className="mb-1">{rewardSummary.exchanged}</h3>
                        <small className="text-muted">交換済み</small>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-3">
                    <div className="card bg-light">
                      <div className="card-body text-center">
                        <h3 className="mb-1">{rewardSummary.unexchanged}</h3>
                        <small className="text-muted">未交換</small>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-3">
                    <div className="card bg-light">
                      <div className="card-body text-center">
                        <h3 className="mb-1">{rewardSummary.granted}</h3>
                        <small className="text-muted">付与済み</small>
                      </div>
                    </div>
                  </div>
                </div>

                {/* 検索フィルター */}
                <div className="row mb-4">
                  <div className="col-md-3">
                    <label className="form-label">期間（開始）</label>
                    <input
                      type="date"
                      className="form-control form-control-sm"
                      value={rewardSearch.periodStart}
                      onChange={(e) =>
                        setRewardSearch({ ...rewardSearch, periodStart: e.target.value })
                      }
                    />
                  </div>
                  <div className="col-md-3">
                    <label className="form-label">期間（終了）</label>
                    <input
                      type="date"
                      className="form-control form-control-sm"
                      value={rewardSearch.periodEnd}
                      onChange={(e) =>
                        setRewardSearch({ ...rewardSearch, periodEnd: e.target.value })
                      }
                    />
                  </div>
                  <div className="col-md-2">
                    <label className="form-label">ステータス</label>
                    <select
                      className="form-select form-select-sm"
                      value={rewardSearch.status}
                      onChange={(e) =>
                        setRewardSearch({ ...rewardSearch, status: e.target.value })
                      }
                    >
                      <option value="">すべて</option>
                      <option value="pending">処理待ち</option>
                      <option value="completed">完了</option>
                      <option value="failed">失敗</option>
                    </select>
                  </div>
                  <div className="col-md-3">
                    <label className="form-label">ユーザーID</label>
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      placeholder="ユーザーID"
                      value={rewardSearch.userId}
                      onChange={(e) =>
                        setRewardSearch({ ...rewardSearch, userId: e.target.value })
                      }
                    />
                  </div>
                  <div className="col-md-1 d-flex align-items-end">
                    <button
                      className="btn btn-primary btn-sm w-100"
                      onClick={loadRewardData}
                      disabled={rewardLoading}
                    >
                      {rewardLoading ? (
                        <span className="spinner-border spinner-border-sm" />
                      ) : (
                        "検索"
                      )}
                    </button>
                  </div>
                </div>

                {/* データテーブル */}
                <div className="table-responsive">
                  <table className="table table-sm table-bordered">
                    <thead className="table-light">
                      <tr>
                        <th>ユーザーID</th>
                        {rewardType === "store" && <th>店舗</th>}
                        <th>ステータス</th>
                        <th>日時</th>
                        {/* 自動付与型は今フェーズでは非表示
                        {rewardType === "auto" && <th>失敗理由</th>}
                        */}
                        <th>付与内容</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rewardData.length === 0 ? (
                        <tr>
                          <td
                            colSpan={rewardType === "store" ? 5 : 4}
                            className="text-center text-muted py-4"
                          >
                            データがありません
                          </td>
                        </tr>
                      ) : (
                        rewardData.map((item, idx) => (
                          <tr key={idx}>
                            <td>{item.userId}</td>
                            {rewardType === "store" && <td>{item.store}</td>}
                            <td>
                              <span
                                className={`badge ${
                                  item.status === "completed"
                                    ? "bg-success"
                                    : item.status === "failed"
                                    ? "bg-danger"
                                    : "bg-warning"
                                }`}
                              >
                                {item.status === "completed"
                                  ? "完了"
                                  : item.status === "failed"
                                  ? "失敗"
                                  : "処理待ち"}
                              </span>
                            </td>
                            <td>{fmtDateTime(item.date)}</td>
                            {/* 自動付与型は今フェーズでは非表示
                            {rewardType === "auto" && (
                              <td className="text-danger">{item.failureReason || "-"}</td>
                            )}
                            */}
                            <td>{item.content}</td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* ========== ユーザー稼働状況タブ ========== */}
          {activeTab === "activity" && (
            <div className="card">
              <div className="card-header d-flex justify-content-between align-items-center">
                <h5 className="mb-0">ユーザー稼働状況</h5>
                <button
                  className="btn btn-outline-secondary btn-sm"
                  onClick={handleActivityCSV}
                  disabled={activityData.length === 0}
                >
                  <i className="fas fa-download me-1"></i>
                  CSV出力
                </button>
              </div>
              <div className="card-body">
                {/* 検索 */}
                <div className="row mb-4">
                  <div className="col-md-6">
                    <div className="input-group">
                      <input
                        type="text"
                        className="form-control"
                        placeholder="ユーザーIDで検索"
                        value={activitySearch.userId}
                        onChange={(e) =>
                          setActivitySearch({ ...activitySearch, userId: e.target.value })
                        }
                      />
                      <button
                        className="btn btn-outline-secondary"
                        onClick={loadActivityData}
                        disabled={activityLoading}
                      >
                        {activityLoading ? (
                          <span className="spinner-border spinner-border-sm" />
                        ) : (
                          "検索"
                        )}
                      </button>
                    </div>
                  </div>
                  <div className="col-md-6 text-end">
                    <small className="text-muted">
                      {activityData.length > 0 && `${activityData.length}件表示中`}
                    </small>
                  </div>
                </div>

                {/* データテーブル */}
                <div className="table-responsive">
                  <table className="table table-sm table-bordered">
                    <thead className="table-light">
                      <tr>
                        <th>ユーザーID</th>
                        <th>達成状況</th>
                        <th>達成日時</th>
                        <th>詳細</th>
                      </tr>
                    </thead>
                    <tbody>
                      {activityData.length === 0 ? (
                        <tr>
                          <td colSpan="4" className="text-center text-muted py-4">
                            データがありません
                          </td>
                        </tr>
                      ) : (
                        activityData.map((item, idx) => (
                          <tr key={idx}>
                            <td>{item.userId}</td>
                            <td>
                              <span className="badge bg-info">
                                {item.achievementStatus || "0/0"}
                              </span>
                            </td>
                            <td>{fmtDateTime(item.achievementDate)}</td>
                            <td>
                              <button
                                className="btn btn-sm btn-outline-primary"
                                onClick={() => handleShowUserDetail(item)}
                              >
                                詳細
                              </button>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* ========== ユーザー詳細モーダル ========== */}
          {showUserDetail && selectedUser && (
            <div className="modal show d-block" style={{ backgroundColor: "rgba(0,0,0,0.5)" }}>
              <div className="modal-dialog modal-lg">
                <div className="modal-content">
                  <div className="modal-header">
                    <h5 className="modal-title">
                      ユーザー詳細: {selectedUser.userId}
                    </h5>
                    <button
                      type="button"
                      className="btn-close"
                      onClick={handleCloseUserDetail}
                    ></button>
                  </div>
                  <div className="modal-body">
                    {/* メッセージ表示 */}
                    {markAchievedMessage.text && (
                      <div className={`alert alert-${markAchievedMessage.type === 'success' ? 'success' : markAchievedMessage.type === 'warning' ? 'warning' : 'danger'} alert-dismissible`}>
                        {markAchievedMessage.text}
                        <button
                          type="button"
                          className="btn-close"
                          onClick={() => setMarkAchievedMessage({ type: '', text: '' })}
                        ></button>
                      </div>
                    )}

                    {/* 達成状況サマリー */}
                    <div className="mb-4">
                      <h6>達成状況</h6>
                      <div className="alert alert-info">
                        <strong>{selectedUser.achievementStatus || "0/0"}</strong> 達成
                      </div>
                    </div>

                    {/* 条件ごとの達成状況 */}
                    <div className="mb-4">
                      <h6>条件ごとの達成状況</h6>
                      <div className="table-responsive">
                        <table className="table table-sm table-bordered">
                          <thead className="table-light">
                            <tr>
                              <th style={{ width: '40px' }}></th>
                              <th>条件名</th>
                              <th>達成状況</th>
                              <th>達成日時</th>
                              <th>操作</th>
                            </tr>
                          </thead>
                          <tbody>
                            {selectedUser.conditions?.map((cond, idx) => (
                              <React.Fragment key={idx}>
                                <tr>
                                  <td className="text-center">
                                    {cond.achieved && cond.details && (
                                      <button
                                        className="btn btn-sm btn-link p-0"
                                        onClick={() => toggleConditionDetails(cond.conditionId)}
                                        style={{ textDecoration: 'none' }}
                                      >
                                        <i className={`fas fa-chevron-${expandedConditions.has(cond.conditionId) ? 'down' : 'right'}`}></i>
                                      </button>
                                    )}
                                  </td>
                                  <td>{cond.name}</td>
                                  <td>
                                    <span
                                      className={`badge ${
                                        cond.achieved ? "bg-success" : "bg-secondary"
                                      }`}
                                    >
                                      {cond.achieved ? "OK" : "未達成"}
                                    </span>
                                  </td>
                                  <td>{cond.achieved ? fmtDateTime(cond.date) : "-"}</td>
                                  <td>
                                    {!cond.achieved && (
                                      <button
                                        className="btn btn-sm btn-outline-warning"
                                        onClick={() => handleMarkAchieved(selectedUser.userId, cond.conditionId)}
                                        disabled={markingAchieved}
                                      >
                                        {markingAchieved ? (
                                          <>
                                            <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                                            処理中...
                                          </>
                                        ) : (
                                          '取得済みに変更'
                                        )}
                                      </button>
                                    )}
                                  </td>
                                </tr>
                                {cond.achieved && expandedConditions.has(cond.conditionId) && cond.details && (
                                  <tr className="table-light">
                                    <td colSpan="5" className="p-3">
                                      <div className="ms-4">
                                        <h6 className="mb-2 text-muted">達成詳細</h6>
                                        <div className="row">
                                          <div className="col-md-4">
                                            <small className="text-muted">位置情報</small>
                                            <p className="mb-1">{cond.details.location || '-'}</p>
                                          </div>
                                          <div className="col-md-4">
                                            <small className="text-muted">対象QR</small>
                                            <p className="mb-1">{cond.details.qrType || '-'}</p>
                                          </div>
                                          <div className="col-md-4">
                                            <small className="text-muted">閲覧ログ対象</small>
                                            <p className="mb-1">{cond.details.viewLogTarget || '-'}</p>
                                          </div>
                                        </div>
                                      </div>
                                    </td>
                                  </tr>
                                )}
                              </React.Fragment>
                            )) || (
                              <tr>
                                <td colSpan="5" className="text-center text-muted">
                                  条件データがありません
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                  <div className="modal-footer">
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={handleCloseUserDetail}
                    >
                      閉じる
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}
